import {
  Box,
  Dropdown,
  DropdownButton,
  DropdownMenu,
  DropdownMenuItem,
  Stack,
  Table,
  TableColumns,
} from '@sixfold/common-ui';
import { notNil, isNil } from '@sixfold/typed-primitives';
import React, { useCallback, useState } from 'react';
import { Link } from 'react-router';

import { OnboardingViewProps } from './company_onboarding';
import { SignupVisibilitySources } from './signup_visibility_sources';
import { StatusRevertedTooltip } from './status_reverted_tooltip';
import { FormattedDate } from '../../../components/date_formatting/formatted_date';
import {
  CompanyOnboardingQuery,
  CompanySignupVisibilitySourceStatus,
  OnboardingActionKey,
  UnifiedCompanySignupEventType,
  UnifiedCompanySignupStatusUpdateType,
} from '../../../lib/graphql';
import { beautifyString } from '../../../lib/util/string';
import {
  getVisibilitySourceLabel,
  getVisibilitySourceStatusExplanation,
  getVisibilitySourceStatusLabel,
} from '../../../relationships/visibility_sources';
import { Routes } from '../../../routes';
import { getSignupStatusName } from '../../utils';

const sourceStatusLegend = [
  CompanySignupVisibilitySourceStatus.ACTIVE,
  CompanySignupVisibilitySourceStatus.IDENTIFIED,
  CompanySignupVisibilitySourceStatus.DEACTIVATED,
];

type SignupEventFromQuery = NonNullable<NonNullable<CompanyOnboardingQuery['company']>['unifiedSignupEvents']>[0];

type SignupEventRow = {
  id: string;
  eventTime: SignupEventFromQuery['eventTime'];
  type: UnifiedCompanySignupEventType;
  triggeredByUser: SignupEventFromQuery['triggeredByUser'];
  visibilitySource: SignupEventFromQuery['visibilitySource'];
};

const signupEventColumns: TableColumns<SignupEventRow> = [
  {
    id: 'eventTime',
    title: 'Time',
    cellContent: ({ value }) => <FormattedDate date={value} />,
  },
  {
    id: 'type',
    title: 'Event',
    cellContent: ({ row }) => (
      <>
        <span>{beautifyString(row.type)}</span>
        {row.visibilitySource !== null ? <span> ({getVisibilitySourceLabel(row.visibilitySource)})</span> : null}
      </>
    ),
  },
  {
    id: 'triggeredByUser',
    title: 'Triggered by',
    cellContent: ({ value }) =>
      value?.user_id !== undefined ? (
        <Link to={Routes.User.generatePath({ user_id: value.user_id })}>
          {value.first_name} {value.last_name}
        </Link>
      ) : (
        '-'
      ),
  },
];

const SignupEventsTable: React.FC<{ events: SignupEventFromQuery[] }> = ({ events }) => {
  if (events.length === 0) {
    return <p style={{ textAlign: 'center' }}>No events</p>;
  }

  const data: SignupEventRow[] = events.map((event, idx) => ({
    id: String(idx),
    eventTime: event.eventTime,
    type: event.type,
    triggeredByUser: event.triggeredByUser,
    visibilitySource: event.visibilitySource,
  }));

  return <Table data={data} columns={signupEventColumns} />;
};

export const SignupStatus: React.FC<OnboardingViewProps> = (props) => {
  const signup = {
    status: props.data.company?.unifiedSignupStatus ?? null,
    events: props.data.company?.unifiedSignupEvents ?? [],
    signupVisibilitySources: props.data.company?.signupVisibilitySources ?? [],
    signupVisibilitySourcesViaSubcarriers: props.data.company?.signupVisibilitySourcesViaSubcarriers ?? [],
    statusTransitionedAt: props.data.company?.signupStatusTransitionedAt ?? null,
    onboardingActionType: props.data.company?.onboardingAction?.type ?? null,
  };

  const statusLabel = notNil(signup.status) ? getSignupStatusName(signup.status) : 'N/A';
  const wasReverted = signup.onboardingActionType === OnboardingActionKey.ONBOARDING_ROLLED_BACK;

  const [isLoading, setIsLoading] = useState(false);

  const updateSignupStatus = useCallback(
    async (update: UnifiedCompanySignupStatusUpdateType) => {
      const companyId = props.data.company?.company_id;

      if (isNil(companyId)) {
        throw new Error('No company to update');
      }

      setIsLoading(true);

      try {
        await props.updateSignupStatus(companyId, { update });
      } finally {
        setIsLoading(false);
      }
    },
    [props],
  );

  return (
    <Stack direction="column" gap="medium">
      <Stack direction="row" gap="medium" alignItems="center" justifyContent="space-between">
        <Box>
          <Stack direction="row" alignItems="center" gap="small">
            <span>
              Onboarding status: <b>{statusLabel}</b>
            </span>
            {wasReverted && (
              <StatusRevertedTooltip statusLabel={statusLabel} transitionedAt={signup.statusTransitionedAt} />
            )}
          </Stack>
        </Box>
        <Box>
          <Dropdown align="end">
            <DropdownButton size="small" loading={isLoading} disabled={isLoading}>
              Update onboarding status
            </DropdownButton>
            <DropdownMenu>
              <DropdownMenuItem
                onClick={() => updateSignupStatus(UnifiedCompanySignupStatusUpdateType.RESET_TO_AUTOMATIC)}>
                Reset to calculated status
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => updateSignupStatus(UnifiedCompanySignupStatusUpdateType.ONBOARDED)}>
                Manually onboarded
              </DropdownMenuItem>
            </DropdownMenu>
          </Dropdown>
        </Box>
      </Stack>
      <div>
        Visibility sources:{' '}
        <SignupVisibilitySources visibilitySources={signup.signupVisibilitySources} events={signup.events} />
      </div>
      <div>
        Visibility sources via subcarriers:{' '}
        <SignupVisibilitySources visibilitySources={signup.signupVisibilitySourcesViaSubcarriers} events={null} />
      </div>
      <div>
        <p style={{ margin: '0 0 4px' }}>Visibility source statuses:</p>
        {sourceStatusLegend.map((status) => (
          <p key={status} style={{ margin: '0 0 4px' }}>
            <span style={{ fontWeight: 600 }}>{getVisibilitySourceStatusLabel(status)}:</span>{' '}
            {getVisibilitySourceStatusExplanation(status)}
          </p>
        ))}
      </div>
      <div>
        <SignupEventsTable events={signup.events} />
      </div>
    </Stack>
  );
};
